import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../lib/data";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const solid = !isHome || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const go = (e, to) => {
    // Anchor links stay real <a> elements for crawlers; intercept for smooth scroll.
    e.preventDefault();
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") { navigate("/"); setTimeout(() => scrollTo(id), 400); }
      else scrollTo(id);
    } else navigate(to);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav
        aria-label="Main"
        className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between gap-4"
      >
        <Logo />

        <ul className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <li key={l.label}>
              <a
                href={l.to}
                onClick={(e) => go(e, l.to)}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                aria-current={location.pathname === l.to ? "page" : undefined}
                className="text-sm font-medium text-navy/80 hover:text-sapphire transition-colors"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/careers"
            data-testid="find-jobs-btn"
            className="px-4 py-2 rounded-full text-sm font-semibold text-navy border border-slate-300 hover:border-navy transition-colors"
          >
            Find Jobs
          </Link>
          <Link
            to="/employers"
            data-testid="find-talent-btn"
            className="px-5 py-2 rounded-full text-sm font-semibold text-white bg-sapphire hover:bg-sapphire-dark transition-colors"
          >
            Find Talent
          </Link>
        </div>

        <button
          type="button"
          className="lg:hidden text-navy p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div id="mobile-menu" className="lg:hidden bg-white border-b border-slate-200 px-4 pb-6 pt-2" data-testid="mobile-menu">
          <ul className="flex flex-col">
            {NAV_LINKS.map((l) => (
              <li key={l.label}>
                <a
                  href={l.to}
                  onClick={(e) => go(e, l.to)}
                  className="block py-3 text-base font-medium text-navy border-b border-slate-100"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
          <div className="flex gap-3 mt-5">
            <Link to="/careers" className="flex-1 text-center px-4 py-2.5 rounded-full text-sm font-semibold text-navy border border-slate-300">Find Jobs</Link>
            <Link to="/employers" className="flex-1 text-center px-4 py-2.5 rounded-full text-sm font-semibold text-white bg-sapphire">Find Talent</Link>
          </div>
        </div>
      )}
    </header>
  );
};
